import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { bulkMoveEdges } from "../../api/edgeApi";
import { getTenantLocations } from "../../api/tenantApi";

const NO_LOCATION = "__none__";

// Seçili edge listesini toplu olarak bir lokasyona taşır. MoveEdgeDialog'un aksine
// (tek edge, tam device/asset/tier payload'lı PUT) burada sadece location_id değişir.
export default function BulkMoveDialog({ open, tenantId, edges, accessToken, onClose, onMoved }) {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [targetLocationId, setTargetLocationId] = useState(NO_LOCATION);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !tenantId) return;
    setTargetLocationId(NO_LOCATION);
    setLoadingLocations(true);
    getTenantLocations(accessToken, tenantId)
      .then((data) => setLocations(data.items || data || []))
      .catch(() => setLocations([]))
      .finally(() => setLoadingLocations(false));
  }, [open, tenantId, accessToken]);

  const handleMove = async () => {
    setSubmitting(true);
    try {
      const locationId = targetLocationId === NO_LOCATION ? null : targetLocationId;
      const results = await bulkMoveEdges(accessToken, edges.map((e) => e.id), locationId);
      const skipped = results.filter((r) => r.status === "skipped");
      if (skipped.length) {
        toast.warning(t("edges.bulkMove.partial", { moved: results.length - skipped.length, total: results.length, skipped: skipped.length }));
      } else {
        toast.success(t("edges.bulkMove.ok", { count: results.length }));
      }
      onMoved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("edges.bulkMove.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } }}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{t("edges.bulkMove.title", { count: edges.length })}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <TextField
          select
          fullWidth
          margin="dense"
          label={t("edges.moveDialog.target")}
          value={targetLocationId}
          onChange={(e) => setTargetLocationId(e.target.value)}
          disabled={loadingLocations}
          helperText={loadingLocations ? t("edges.edit.locationsLoading") : undefined}
        >
          <MenuItem value={NO_LOCATION}>{t("edges.tree.noLocation")}</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name} {loc.address ? `- ${loc.address}` : ""}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ color: "var(--text-muted)" }}>{t("edges.cancel")}</Button>
        <Button onClick={handleMove} disabled={submitting} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>
          {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : t("edges.move")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
